import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";

var _dec, _dec2, _dec3, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
// TODO: remove delimiter comment description once the deprecated values are removed
import React, { Children, Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { View } from '@instructure/ui-view';
import { themeable, ThemeablePropTypes } from '@instructure/ui-themeable';
import { passthroughProps, safeCloneElement, deprecated } from '@instructure/ui-react-utils';
import { Children as ChildrenPropTypes } from '@instructure/ui-prop-types';
import { testable } from '@instructure/ui-testable';
import { InlineList } from "../InlineList/index.js";
import { ListItem } from "./ListItem/index.js";
var styles = {
  componentId: 'fClCc',
  template: function template(theme) {
    return "\n\n.fClCc_bGBk{-webkit-padding-end:0;-webkit-padding-start:".concat(theme.listPadding || 'inherit', ";padding-inline-end:0;padding-inline-start:").concat(theme.listPadding || 'inherit', "}\n\n[dir=ltr] .fClCc_bGBk{padding-left:").concat(theme.listPadding || 'inherit', ";padding-right:0}\n\n[dir=rtl] .fClCc_bGBk{padding-left:0;padding-right:").concat(theme.listPadding || 'inherit', "}\n\n.fClCc_bGBk.fClCc_enMC{-webkit-padding-end:0;-webkit-padding-start:calc(").concat(theme.listPadding || 'inherit', "/2);counter-reset:ol-counter;list-style-type:none;padding-inline-end:0;padding-inline-start:calc(").concat(theme.listPadding || 'inherit', "/2)}\n\n[dir=ltr] .fClCc_bGBk.fClCc_enMC{padding-left:calc(").concat(theme.listPadding || 'inherit', "/2);padding-right:0}\n\n[dir=rtl] .fClCc_bGBk.fClCc_enMC{padding-left:0;padding-right:calc(").concat(theme.listPadding || 'inherit', "/2)}\n\n.fClCc_bGBk.fClCc_enMC li:before{-webkit-margin-end:").concat(theme.orderedNumberMargin || 'inherit', ";-webkit-margin-start:0;content:counters(ol-counter,\".\") \".\";counter-increment:ol-counter;font-weight:").concat(theme.orderedNumberFontWeight || 'inherit', ";margin-inline-end:").concat(theme.orderedNumberMargin || 'inherit', ";margin-inline-start:0}\n\n[dir=ltr] .fClCc_bGBk.fClCc_enMC li:before{margin-left:0;margin-right:").concat(theme.orderedNumberMargin || 'inherit', "}\n\n[dir=rtl] .fClCc_bGBk.fClCc_enMC li:before{margin-left:").concat(theme.orderedNumberMargin || 'inherit', ";margin-right:0}\n\n.fClCc_bGBk.fClCc_fLbg{-webkit-padding-start:0;list-style-type:none;padding-inline-start:0}\n\n[dir=ltr] .fClCc_bGBk.fClCc_fLbg{padding-left:0}\n\n[dir=rtl] .fClCc_bGBk.fClCc_fLbg{padding-right:0}");
  },
  'root': 'fClCc_bGBk',
  'ordered': 'fClCc_enMC',
  'unstyled': 'fClCc_fLbg'
};
import theme from "./theme.js";
/**
---
category: components
---
**/

var List = (_dec = deprecated('8.0.0', {
  variant: 'List with the isUnstyled boolean or InlineList'
}), _dec2 = testable(), _dec3 = themeable(theme, styles), _dec(_class = _dec2(_class = _dec3(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(List, _Component);

  var _super = _createSuper(List);

  function List() {
    _classCallCheck(this, List);

    return _super.apply(this, arguments);
  }

  _createClass(List, [{
    key: "renderChildren",
    value: function renderChildren() {
      var _this = this;

      return Children.map(this.props.children, function (child) {
        if (!child) return; // ignore null, falsy children

        return safeCloneElement(child, {
          isUnstyled: _this.props.isUnstyled,
          delimiter: _this.props.delimiter,
          size: _this.props.size,
          spacing: _this.props.itemSpacing
        });
      });
    }
  }, {
    key: "renderInlineChild",
    value: function renderInlineChild() {
      return Children.map(this.props.children, function (child) {
        if (!child) return;
        return /*#__PURE__*/React.createElement(InlineList.Item, child.props);
      });
    }
  }, {
    key: "render",
    value: function render() {
      var _classes;

      var _this$props = this.props,
          as = _this$props.as,
          margin = _this$props.margin,
          isUnstyled = _this$props.isUnstyled,
          elementRef = _this$props.elementRef,
          variant = _this$props.variant,
          rest = _objectWithoutProperties(_this$props, ["as", "margin", "isUnstyled", "elementRef", "variant"]);

      var classes = (_classes = {}, _defineProperty(_classes, styles.root, true), _defineProperty(_classes, styles.unstyled, isUnstyled === true || variant === 'unstyled'), _defineProperty(_classes, styles.ordered, as === 'ol'), _defineProperty(_classes, styles.inline, variant === 'inline'), _classes);

      if (!variant || variant === 'default' || variant === 'unstyled') {
        return /*#__PURE__*/React.createElement(View, Object.assign({}, passthroughProps(rest), {
          className: classnames(classes),
          as: as,
          margin: margin,
          elementRef: elementRef,
          display: "block"
        }), this.renderChildren());
      } else {
        return /*#__PURE__*/React.createElement(InlineList, this.props, this.renderInlineChild());
      }
    }
  }]);

  List.displayName = "List";
  return List;
}(Component), _class2.propTypes = {
  /**
   * Only accepts `<List.Item>` as a child
   */
  children: ChildrenPropTypes.oneOf([ListItem]),
  as: PropTypes.oneOf(['ul', 'ol']),

  /**
   * One of: none, dashed, solid
   */
  delimiter: deprecated.deprecatePropValues(PropTypes.oneOf(['none', 'dashed', 'solid', 'pipe', 'slash', 'arrow']), ['pipe', 'slash', 'arrow'], function (_ref) {
    var propValue = _ref.propValue;
    return "with 'delimiter' set to ".concat(propValue, " will only be available when using [InlineList] as of version 8.0.0.");
  }),

  /**
   * When set, renders the List Items without a list style type.
   */
  isUnstyled: PropTypes.bool,

  /**
   * Valid values are `0`, `none`, `auto`, `xxx-small`, `xx-small`, `x-small`,
   * `small`, `medium`, `large`, `x-large`, `xx-large`. Apply these values via
   * familiar CSS-like shorthand. For example: `margin="small auto large"`.
   */
  margin: ThemeablePropTypes.spacing,
  size: PropTypes.oneOf(['small', 'medium', 'large']),

  /**
   * Sets the margin separating each ListItem.
   */
  itemSpacing: PropTypes.oneOf(['none', 'xxx-small', 'xx-small', 'x-small', 'small', 'medium', 'large', 'x-large', 'xx-large']),
  elementRef: PropTypes.func,

  /**
   * __deprecated__ Option will be to use the isUnstyled prop or the InlineList component
   */
  variant: PropTypes.oneOf(['default', 'unstyled', 'inline'])
}, _class2.defaultProps = {
  children: null,
  as: 'ul',
  delimiter: 'none',
  isUnstyled: false,
  margin: void 0,
  size: 'medium',
  itemSpacing: 'none',
  elementRef: function elementRef(el) {},
  variant: void 0
}, _class2.Item = ListItem, _temp)) || _class) || _class) || _class);
export default List;
export { List, ListItem };